import type { OrganizationUser } from '@compass/client/types/organization_user';
import { AlertTriangle, Loader2 } from 'lucide-react';
import React from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useRemoveProgramUserRole } from '@/hooks/useUserRoles';

interface RemoveProgramMemberDialogProps {
  organizationId: string;
  programId: string;
  user: OrganizationUser | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const RemoveProgramMemberDialog: React.FC<RemoveProgramMemberDialogProps> = ({
  organizationId,
  programId,
  user,
  open,
  onOpenChange,
}) => {
  const removeMutation = useRemoveProgramUserRole();

  // Get role directly from user object (passed from search API)
  const userRole = user?.roles && user.roles.length > 0 ? user.roles[0] : null;

  const handleRemove = async () => {
    if (!user?.user_id || !userRole?.id) return;

    try {
      await removeMutation.mutateAsync({
        organization_id: organizationId,
        program_id: programId,
        user_id: user.user_id,
        role_id: userRole.id,
      });

      toast.success(`${user.name || user.email} has been removed from the program`);
      onOpenChange(false);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || error?.message || 'Failed to remove user from program',
      );
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Remove Member
          </DialogTitle>
          <DialogDescription className="pt-4">
            Are you sure you want to remove{' '}
            <span className="font-medium">{user.name || user.email}</span> from this program?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={removeMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleRemove}
            disabled={removeMutation.isPending || !userRole}
          >
            {removeMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Remove Member
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
